import { useEffect } from "react";
import Layout from "@/components/layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Code, Database, Layers, Settings, Link as LinkIcon, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

const customSolutions = [
{
  id: 1,
  title: "Resume Search Automation",
  description: "Automate the process of searching, filtering, and analyzing resumes from job boards like Seek, Indeed, and LinkedIn.",
  icon: <Database className="h-12 w-12 text-blue-600" data-id="202tk95a7" data-path="src/pages/CustomSolutionsPage.tsx" />,
  benefits: [
  "Save hours of manual resume searching",
  "Identify the most qualified candidates quickly",
  "Automatic candidate ranking and scoring",
  "Integration with your existing ATS"]

},
{
  id: 2,
  title: "Google Sheets ↔ ChatGPT Workflow",
  description: "Connect your spreadsheet data to ChatGPT for intelligent analysis, content generation, and data transformation.",
  icon: <Layers className="h-12 w-12 text-purple-600" data-id="x03dpu7qp" data-path="src/pages/CustomSolutionsPage.tsx" />,
  benefits: [
  "Automate spreadsheet data analysis",
  "Generate content based on your data",
  "Transform data with AI intelligence",
  "No coding required for setup"]

},
{
  id: 3,
  title: "WhatsApp Calling Reply System",
  description: "AI-powered system that automatically responds to WhatsApp messages and calls with contextual awareness.",
  icon: <MessageSquare className="h-12 w-12 text-green-600" data-id="ee08hj70u" data-path="src/pages/CustomSolutionsPage.tsx" />,
  benefits: [
  "Never miss important messages",
  "Automatic prioritization of inquiries",
  "Custom responses based on message content",
  "Seamless handoff to human agents when needed"]

},
{
  id: 4,
  title: "Custom API Integrations",
  description: "Connect any systems together with custom API integrations built specifically for your business needs.",
  icon: <Code className="h-12 w-12 text-gray-700" data-id="5anm7wmym" data-path="src/pages/CustomSolutionsPage.tsx" />,
  benefits: [
  "Connect disparate systems seamlessly",
  "Automate data flow between applications",
  "Eliminate manual data entry",
  "Custom workflows tailored to your business"]

},
{
  id: 5,
  title: "Enterprise Integration Platform",
  description: "A comprehensive platform connecting all your business tools with AI processing and workflow automation.",
  icon: <Settings className="h-12 w-12 text-blue-600" data-id="ganh5w3rb" data-path="src/pages/CustomSolutionsPage.tsx" />,
  benefits: [
  "Central hub for all business operations",
  "AI-powered data processing and insights",
  "Custom dashboards and reporting",
  "Scalable for enterprise needs"]

},
{
  id: 6,
  title: "Multi-Platform Data Connector",
  description: "Connect data sources from multiple platforms for unified analytics and automation.",
  icon: <LinkIcon className="h-12 w-12 text-purple-600" data-id="kr0yaye7u" data-path="src/pages/CustomSolutionsPage.tsx" />,
  benefits: [
  "Unified view of all business data",
  "Automated reporting across platforms",
  "Real-time data synchronization",
  "Custom triggers and actions"]

}];


export default function CustomSolutionsPage() {
  useEffect(() => {
    // Set page title
    document.title = "Custom AI Automation Solutions | AI Automation Co.";
  }, []);

  return (
    <Layout data-id="svxdq5dn7" data-path="src/pages/CustomSolutionsPage.tsx">
      {/* Header section */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-16" data-id="1pgi3ltmv" data-path="src/pages/CustomSolutionsPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="1bvh4vvp2" data-path="src/pages/CustomSolutionsPage.tsx">
          <div className="max-w-3xl mx-auto text-center" data-id="shbspo34q" data-path="src/pages/CustomSolutionsPage.tsx">
            <h1 className="text-4xl font-bold text-gray-900 mb-6" data-id="2acq56g5b" data-path="src/pages/CustomSolutionsPage.tsx">
              Custom AI Automation Solutions
            </h1>
            <p className="text-lg text-gray-600 mb-8" data-id="21wdl4crs" data-path="src/pages/CustomSolutionsPage.tsx">
              Beyond our standard offerings, we create custom AI solutions tailored specifically to your unique business challenges. Our team of experts will work with you to develop automation solutions that integrate seamlessly with your existing workflows.
            </p>
            <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" data-id="cg9jao85a" data-path="src/pages/CustomSolutionsPage.tsx">
              <Link to="/contact" data-id="pybyq5liw" data-path="src/pages/CustomSolutionsPage.tsx">Discuss Your Custom Solution</Link>
            </Button>
          </div>
        </div>
      </div>
      
      {/* Custom solutions section */}
      <div className="py-16 bg-white" data-id="gw06uv8ld" data-path="src/pages/CustomSolutionsPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="lc9guyog0" data-path="src/pages/CustomSolutionsPage.tsx">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="5hh009y54" data-path="src/pages/CustomSolutionsPage.tsx">
            {customSolutions.map((solution) =>
            <Card key={solution.id} className="hover:shadow-lg transition-shadow border border-gray-200" data-id="8y81ijl3y" data-path="src/pages/CustomSolutionsPage.tsx">
                <CardContent className="p-6" data-id="ak9aweb6p" data-path="src/pages/CustomSolutionsPage.tsx">
                  <div className="mb-6" data-id="pz890diut" data-path="src/pages/CustomSolutionsPage.tsx">{solution.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="k16kvdn7z" data-path="src/pages/CustomSolutionsPage.tsx">{solution.title}</h3>
                  <p className="text-gray-600 mb-4" data-id="nao37z9ke" data-path="src/pages/CustomSolutionsPage.tsx">{solution.description}</p>
                  
                  <h4 className="font-medium text-gray-900 mb-2" data-id="82di9icdl" data-path="src/pages/CustomSolutionsPage.tsx">Key Benefits:</h4>
                  <ul className="space-y-1 mb-6" data-id="jss8wroyh" data-path="src/pages/CustomSolutionsPage.tsx">
                    {solution.benefits.map((benefit, index) =>
                  <li key={index} className="flex items-start" data-id="n1kj82bak" data-path="src/pages/CustomSolutionsPage.tsx">
                        <div className="text-green-500 mr-2" data-id="jcnlg8pav" data-path="src/pages/CustomSolutionsPage.tsx">✓</div>
                        <span className="text-gray-600 text-sm" data-id="4f3jeego5" data-path="src/pages/CustomSolutionsPage.tsx">{benefit}</span>
                      </li>
                  )}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      
      {/* API integration section */}
      <div className="py-16 bg-gray-50" data-id="jxln5qkhu" data-path="src/pages/CustomSolutionsPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="l2z3mcs9e" data-path="src/pages/CustomSolutionsPage.tsx">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="6m46pd58z" data-path="src/pages/CustomSolutionsPage.tsx">
            <div data-id="h48nq8nu2" data-path="src/pages/CustomSolutionsPage.tsx">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="h91g0yb73" data-path="src/pages/CustomSolutionsPage.tsx">
                Seamless Integration with Your Existing Tools
              </h2>
              <p className="text-lg text-gray-600 mb-8" data-id="eh8nlamg5" data-path="src/pages/CustomSolutionsPage.tsx">
                Our solutions connect with your existing business tools through secure API integrations. We support custom workflows for any industry or use case, with end-to-end implementation handled by our expert team.
              </p>
              <div className="space-y-4" data-id="3x5scerdr" data-path="src/pages/CustomSolutionsPage.tsx">
                <div className="flex items-start" data-id="0n86ui0k3" data-path="src/pages/CustomSolutionsPage.tsx">
                  <div className="text-blue-500 mr-3 mt-1" data-id="crt1ytuh6" data-path="src/pages/CustomSolutionsPage.tsx">✓</div>
                  <div data-id="eqy5y1z49" data-path="src/pages/CustomSolutionsPage.tsx">
                    <h3 className="font-medium text-gray-900" data-id="okezaqrkz" data-path="src/pages/CustomSolutionsPage.tsx">Secure API Connections</h3>
                    <p className="text-gray-600" data-id="rmhgva73f" data-path="src/pages/CustomSolutionsPage.tsx">Enterprise-grade security for all integrations</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="74f4a9w8u" data-path="src/pages/CustomSolutionsPage.tsx">
                  <div className="text-blue-500 mr-3 mt-1" data-id="scvh39ax5" data-path="src/pages/CustomSolutionsPage.tsx">✓</div>
                  <div data-id="x41bnabh3" data-path="src/pages/CustomSolutionsPage.tsx">
                    <h3 className="font-medium text-gray-900" data-id="m9swkcvg4" data-path="src/pages/CustomSolutionsPage.tsx">Custom Workflow Design</h3>
                    <p className="text-gray-600" data-id="gp503rruk" data-path="src/pages/CustomSolutionsPage.tsx">Tailored to your exact business processes</p>
                  </div>
                </div>
                <div className="flex items-start" data-id="ajozc5b1i" data-path="src/pages/CustomSolutionsPage.tsx">
                  <div className="text-blue-500 mr-3 mt-1" data-id="putcwo8ae" data-path="src/pages/CustomSolutionsPage.tsx">✓</div>
                  <div data-id="nuv6w9ikf" data-path="src/pages/CustomSolutionsPage.tsx">
                    <h3 className="font-medium text-gray-900" data-id="d4qcuwioy" data-path="src/pages/CustomSolutionsPage.tsx">End-to-End Implementation</h3>
                    <p className="text-gray-600" data-id="ol03wk4ef" data-path="src/pages/CustomSolutionsPage.tsx">From design to deployment and ongoing support</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative" data-id="owcgv7pcu" data-path="src/pages/CustomSolutionsPage.tsx">
              <div className="aspect-video bg-white rounded-lg shadow-xl p-8 flex items-center justify-center" data-id="u5u0r2bkx" data-path="src/pages/CustomSolutionsPage.tsx">
                <img
                  src="https://images.unsplash.com/photo-1531973576160-7125cd663d86?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3MTg3MTl8MHwxfHNlYXJjaHwxfHxBJTIwdmlzdWFsbHklMjBhcHBlYWxpbmclMjBpbWFnZSUyMHNob3djYXNpbmclMjBhJTIwd29ya3NwYWNlJTIwd2l0aCUyMGElMjBsYXB0b3AlMjBhbmQlMjBvdGhlciUyMG9mZmljZSUyMGl0ZW1zJTJDJTIwZW1waGFzaXppbmclMjBwcm9kdWN0aXZpdHklMjBhbmQlMjB0ZWNobm9sb2d5LnxlbnwwfHx8fDE3NDc3MTE0MzN8MA&ixlib=rb-4.1.0&q=80&w=200$w=800"
                  alt="API Integration"
                  className="rounded max-h-full max-w-full" data-id="l7idu0vnh" data-path="src/pages/CustomSolutionsPage.tsx" />

              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* CTA section */}
      <div className="py-16 bg-white" data-id="ovwlrb1xa" data-path="src/pages/CustomSolutionsPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="i3ufy9e0q" data-path="src/pages/CustomSolutionsPage.tsx">
          <div className="max-w-3xl mx-auto text-center" data-id="s5gwlzywg" data-path="src/pages/CustomSolutionsPage.tsx">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="1m0bn9e3n" data-path="src/pages/CustomSolutionsPage.tsx">
              Ready to Discuss Your Custom Solution?
            </h2>
            <p className="text-lg text-gray-600 mb-8" data-id="kbm0vskrq" data-path="src/pages/CustomSolutionsPage.tsx">
              Our team of experts is ready to learn about your business challenges and design a custom automation solution that meets your specific needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="s9vxc74y9" data-path="src/pages/CustomSolutionsPage.tsx">
              <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" data-id="meov48wl2" data-path="src/pages/CustomSolutionsPage.tsx">
                <Link to="/contact" data-id="bpibxoy79" data-path="src/pages/CustomSolutionsPage.tsx">Book a Consultation</Link>
              </Button>
              <Button asChild size="lg" className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white" data-id="0c0yjh5bv" data-path="src/pages/CustomSolutionsPage.tsx">
                <Link to="/case-studies" data-id="pz14jlcir" data-path="src/pages/CustomSolutionsPage.tsx">View Case Studies</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>);

}